'use client'

import {
  BRAND_LOGO_IMAGE_HEIGHT,
  BRAND_LOGO_IMAGE_WIDTH,
} from '@/feature/brand/services/brand.constants'
import { getEntryOpensInNewTab } from '@/feature/header/services/header.utils'
import { toMenuEntryPage } from '@/feature/navigation/services/navigation.routing'
import { twMerge } from '@/lib/classname/twMerge'
import { ensureLeadingSlash } from '@/lib/utils/link-utils'
import { Container } from '@/ui/layouts/container/Container'
import {
  BLOCK_IMAGE_LINK_CLASSNAME,
  BLOCK_IMAGE_LINK_MODE,
  BLOCK_IMAGE_LINK_PICTURE_CLASSNAME,
  BlockImageLink,
} from '@/ui/links/block-image-link/BlockImageLink.client'
import { CATEGORY_IMAGE_SIZE_ICON } from '@/ui/media/cloudflare-picture-category/CloudflarePictureCategory'
import { CloudflarePicture } from '@/ui/media/cloudflare-picture/CloudflarePicture.client'
import { MenuPopin } from '@/ui/popin/menu-popin/MenuPopin'
import type { MenuEntryFragment } from '@gql-generated/graphql'
import { Dialog, DialogTrigger, Modal } from 'react-aria-components'
import sanitizeHtml from 'sanitize-html'

type EntryImage = MenuEntryFragment['image']
export type SubEntry = NonNullable<NonNullable<MenuEntryFragment['entries']>[number]>

/**
 * Resolves the imageComponent prop for a BlockImageLink entry.
 * Category entries render a square 56px product icon; brand entries with an image render a wide logo.
 */
function resolveEntryImageComponent(
  isCategory: boolean,
  isBrand: boolean,
  image: EntryImage,
  entryName: MenuEntryFragment['name'],
) {
  const sharedProps = {
    src: image?.set?.[0]?.url,
    alt: image?.alt ?? entryName ?? '',
    mode: BLOCK_IMAGE_LINK_MODE,
    isInternalPicture: true,
  } as const

  if (isCategory) {
    return (
      <CloudflarePicture
        {...sharedProps}
        width={CATEGORY_IMAGE_SIZE_ICON}
        className={BLOCK_IMAGE_LINK_CLASSNAME}
        pictureClassName={BLOCK_IMAGE_LINK_PICTURE_CLASSNAME}
      />
    )
  }

  if (isBrand && image) {
    return (
      <CloudflarePicture
        {...sharedProps}
        width={BRAND_LOGO_IMAGE_WIDTH}
        height={BRAND_LOGO_IMAGE_HEIGHT}
        className="max-w-[6.25rem]"
        pictureClassName="flex h-5 w-auto min-w-20 justify-center text-center"
      />
    )
  }

  return undefined
}

export type SliderTabEntryProps = {
  /** The sub-entry (level 2) to render as a card */
  entry: SubEntry
  /** Whether the parent tab is a brand context (slug = "brand") */
  isBrand: boolean
  /** Whether the parent tab is a product context (has MenuEntryCategory children) */
  isProduct: boolean
  /** Top-level entry name — used in tracking event labels */
  parentName: string | null | undefined
  /** i18n label for the "all categories" title in product popins */
  menuPopinParentCategoryText: string
  /** CSS position class applied to the popin modal */
  menuPopinPositionClass: string
  /** Key of the currently opened popin */
  selectedCategoryKey: string | null
  setSelectedCategoryKey: React.Dispatch<React.SetStateAction<string | null>>
  handleMenuPopinClose: () => void
}

export function SliderTabEntry({
  entry,
  isBrand,
  isProduct,
  parentName,
  menuPopinParentCategoryText,
  menuPopinPositionClass,
  selectedCategoryKey,
  setSelectedCategoryKey,
  handleMenuPopinClose,
}: SliderTabEntryProps) {
  // Exclude seoContent from propsEntry so it doesn't reach toMenuEntryPage unexpectedly
  const {
    name: entryName,
    seoContent: _seoContent,
    image,
    entries: subEntries,
    ...propsEntry
  } = entry

  const isCategory = propsEntry.__typename === 'MenuEntryCategory'
  const clonedSubEntries = subEntries ? [...subEntries] : []

  // For product context: use generic label, keep all sub-entries.
  // For other contexts: first sub-entry becomes the popin title and is shifted out.
  let menuPopinTitle = isProduct ? menuPopinParentCategoryText : ''

  if (!isProduct && clonedSubEntries.length > 0) {
    menuPopinTitle = clonedSubEntries.shift()?.name || menuPopinTitle
  }

  // After potential shift, determine if the popin has content to show
  const hasPopinContent = clonedSubEntries.length > 0

  const entryHref =
    !hasPopinContent && propsEntry.__typename === 'MenuEntryLink' && propsEntry.url
      ? ensureLeadingSlash(propsEntry.url)
      : undefined

  const entryOpensInNewTab = getEntryOpensInNewTab(propsEntry)

  return (
    <div className="w-20">
      <DialogTrigger>
        <BlockImageLink
          onPress={
            hasPopinContent
              ? () => {
                  if (entryName) setSelectedCategoryKey(entryName)
                }
              : undefined
          }
          href={entryHref}
          opensInNewTab={entryOpensInNewTab}
          image={!isCategory ? (image ?? undefined) : undefined}
          imageComponent={resolveEntryImageComponent(isCategory, isBrand, image, entryName)}
          isActive={selectedCategoryKey === entryName}
          trackingData={{
            event: 'gaEvent',
            eventCategory: 'menu',
            eventAction: 'niveau 2',
            eventLabel: `${parentName} | ${entryName}`,
          }}
        >
          {!(isBrand && image) && entryName && (
            <span
              className={!image && !isCategory ? 'font-bold' : undefined}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(entryName) }}
            />
          )}
        </BlockImageLink>

        <Modal
          isOpen={selectedCategoryKey === entryName}
          onOpenChange={(open: boolean) => {
            if (open && entryName) {
              setSelectedCategoryKey(entryName)
            } else {
              handleMenuPopinClose()
            }
          }}
          className={twMerge(
            'pointer-events-none hidden h-full desktop:block',
            menuPopinPositionClass,
          )}
        >
          {!!clonedSubEntries.length && (
            <Dialog>
              {({ close }) => (
                <div className={menuPopinPositionClass}>
                  <Container>
                    <MenuPopin
                      title={{
                        name: menuPopinTitle || '',
                        url: toMenuEntryPage(propsEntry),
                        opensInNewTab: entryOpensInNewTab,
                      }}
                      {...(isProduct && {
                        trackingData: {
                          event: 'gaEvent',
                          eventCategory: 'menu',
                          eventAction: 'niveau 3',
                          eventLabel: `${parentName} | ${entryName} | Toute la catégorie`,
                        },
                      })}
                      categories={clonedSubEntries.map(
                        ({ name: subEntryName, ...propsSubEntry }) => ({
                          name: subEntryName || '',
                          url: toMenuEntryPage(propsSubEntry),
                          opensInNewTab: getEntryOpensInNewTab(propsSubEntry),
                          trackingData: isProduct
                            ? {
                                event: 'gaEvent',
                                eventCategory: 'menu',
                                eventAction: 'niveau 3',
                                eventLabel: `${parentName} | ${entryName} | ${subEntryName}`,
                              }
                            : undefined,
                        }),
                      )}
                      onClose={close}
                      className="pointer-events-auto w-full"
                    />
                  </Container>
                </div>
              )}
            </Dialog>
          )}
        </Modal>
      </DialogTrigger>
    </div>
  )
}
